import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import { readAttribution } from '../utils/attribution';
import './WebsitesAdPage.css';

// Google Ads landing page. Ad-only prices: noindex, nofollow, and deliberately
// absent from the sitemap, llms.txt, nav, footer and every internal link.
const ENDPOINT = 'https://audit.ubotika.site/enterprise';
const SOURCE = 'google-web-design';
const PHONE_DISPLAY = '(657) 835-0873';
const PHONE_TEL = 'tel:+16578350873';

export const PACKAGES = [
  { name: 'Website (up to 5 pages)', price: '$495 one-time + $39/month' },
  { name: 'Landing page', price: '$500 one-time + $49/month' },
  { name: 'Website + landing page', price: '$600 one-time + $79/month' },
  { name: 'Promo video', price: '$300 per video' },
  { name: 'Chatbot for your site', price: '$400 setup + $99/month' },
  { name: 'AI phone receptionist (24/7)', price: '$500 setup + $399/month' },
];

function LeadForm() {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [need, setNeed] = useState('');
  const [note, setNote] = useState('');
  const [hp, setHp] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (hp || status === 'sending') return;
    if (!name.trim()) { setErrorMsg('Please enter your name.'); return; }
    if (phone.replace(/\D/g, '').length < 10) { setErrorMsg('Please enter a phone number we can reach you on.'); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) { setErrorMsg('Please enter a valid email address.'); return; }
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          role: need,
          note: note.trim(),
          source: SOURCE,
          landingPath: window.location.pathname,
          utm: readAttribution(),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      // Fire once, and only after the server accepted the lead.
      if (window.gtag) {
        window.gtag('event', 'conversion', { send_to: 'AW-18245536456/Pz-jCJ6LtsAcEMiVk_xD' });
        window.gtag('event', 'web_design_lead', { source: SOURCE });
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(`Could not send. Please call ${PHONE_DISPLAY} instead.`);
    }
  };

  if (status === 'success') {
    return (
      <div className="lpw-form lpw-form--done" id="quote">
        <h2>Got it, {name.trim().split(' ')[0]}.</h2>
        <p>
          We&apos;ll call you at {phone.trim()} within one business day. Want to talk
          now? Call <a href={PHONE_TEL}>{PHONE_DISPLAY}</a>.
        </p>
      </div>
    );
  }

  return (
    <form className="lpw-form" id="quote" onSubmit={onSubmit} noValidate>
      <h2>Get started</h2>
      <div className="lpw-field">
        <label htmlFor="lpw-name">Name</label>
        <input id="lpw-name" type="text" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
      </div>
      <div className="lpw-field">
        <label htmlFor="lpw-phone">Phone</label>
        <input id="lpw-phone" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required autoComplete="tel" />
      </div>
      <div className="lpw-field">
        <label htmlFor="lpw-email">Email</label>
        <input id="lpw-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
      </div>
      <div className="lpw-field">
        <label htmlFor="lpw-need">What do you need? <span>(optional)</span></label>
        <select id="lpw-need" value={need} onChange={(e) => setNeed(e.target.value)}>
          <option value="">Pick one…</option>
          {PACKAGES.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
        </select>
      </div>
      <div className="lpw-field">
        <label htmlFor="lpw-note">Anything else? <span>(optional)</span></label>
        <textarea id="lpw-note" rows="2" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <input
        type="text" name="website_url_confirm" tabIndex="-1" autoComplete="off"
        aria-hidden="true" className="lpw-hp" value={hp} onChange={(e) => setHp(e.target.value)}
      />
      {errorMsg && <p className="lpw-error">{errorMsg}</p>}
      <button type="submit" className="lpw-submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Request my website'}
      </button>
      <p className="lpw-fine">
        Or call <a href={PHONE_TEL}>{PHONE_DISPLAY}</a>. We only use your details to reply to you.
      </p>
    </form>
  );
}

function WebsitesAdPage() {
  return (
    <>
      <SEO
        title="Fixed-Price Business Websites | UBOTIKA"
        description="A professional website for your business, live in two weeks, at a fixed price. Websites, landing pages, promo videos, chatbots and AI phone receptionists."
        path="/lp/websites"
        noindex
        nofollow
      />
      <header className="lpw-topbar">
        <span className="lpw-brand">
          <img src="/ubotika-logo.png" alt="" aria-hidden="true" />
          <span>UBOTIKA</span>
        </span>
        <a className="lpw-call" href={PHONE_TEL}>Call {PHONE_DISPLAY}</a>
      </header>
      <main id="main-content" className="lpw-page">
        <section className="lpw-hero">
          <h1>A professional website for your business, live in two weeks. Fixed price.</h1>
        </section>

        <section className="lpw-body">
          <div className="lpw-prices">
            <table className="lpw-table">
              <caption>Packages and prices</caption>
              <tbody>
                {PACKAGES.map((p) => (
                  <tr key={p.name}>
                    <th scope="row">{p.name}</th>
                    <td>{p.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="lpw-covers">
              The monthly fee covers hosting, fixes if anything breaks, and small updates.
            </p>
            <p className="lpw-excluded">
              <strong>Not included, paid by you directly:</strong> your domain name, payment
              processing fees (Stripe or PayPal), and business email accounts.
            </p>
          </div>
          <LeadForm />
        </section>
      </main>
      <footer className="lpw-footer">
        <span>&copy; {new Date().getFullYear()} UBOTIKA · Los Angeles, CA</span>
        <span>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms</Link>
        </span>
      </footer>
    </>
  );
}

export default WebsitesAdPage;
